<template>
  <div class="cook-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">CUISINE CENTRALE</h1>
      <p class="dashboard-subtitle">Statistiques & Retours</p>
    </div>

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Top recettes -->
      <div class="card card-featured" @click="showTopRecipes = true">
        <div class="card-header">
          <h3 class="card-title">🏆 Top recettes</h3>
          <span class="badge badge-green">Cette semaine</span>
        </div>
        <p class="card-description">Les 3 plats les mieux notés par les écoles</p>
        <button class="card-link">Voir le classement →</button>
      </div>

      <!-- Card 2: À améliorer -->
      <div class="card" @click="showToImprove = true">
        <div class="card-header">
          <h3 class="card-title">⚠️ À améliorer</h3>
          <span class="badge badge-orange">{{ toImprove.length }}</span>
        </div>
        <p class="card-description">Recettes avec retours négatifs</p>
        <button class="card-link">Consulter →</button>
      </div>

      <!-- Card 3: Retours des écoles -->
      <div class="card" @click="showSchoolFeedback = true">
        <div class="card-header">
          <h3 class="card-title">📢 Retours écoles</h3>
          <span class="badge badge-blue">3 nouveaux</span>
        </div>
        <p class="card-description">Messages et remarques des cantines</p>
        <button class="card-link">Lire →</button>
      </div>

      <!-- Card 4: Planification -->
      <div class="card" @click="showPlanning = true">
        <div class="card-header">
          <h3 class="card-title">🗓️ Planification</h3>
        </div>
        <p class="card-description">Menus à venir et historique des plats</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 5: Gaspillage réseau -->
      <div class="card" @click="showWasteStats = true">
        <div class="card-header">
          <h3 class="card-title">♻️ Gaspillage réseau</h3>
          <span class="badge badge-green">-15%</span>
        </div>
        <p class="card-description">Évolution du gaspillage sur toutes les écoles</p>
        <button class="card-link">Analyser →</button>
      </div>

      <!-- Card 6: Statistiques complètes -->
      <div class="card" @click="showAllStats = true">
        <div class="card-header">
          <h3 class="card-title">📊 Toutes les stats</h3>
        </div>
        <p class="card-description">Analyses détaillées et tendances</p>
        <button class="card-link">Consulter →</button>
      </div>
    </div>

    <!-- Modal: Top recettes -->
    <div v-if="showTopRecipes" class="modal-overlay" @click="showTopRecipes = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showTopRecipes = false">✕</button>
        <h2 class="modal-title">🏆 Top 3 de la semaine</h2>

        <div class="ranking-list">
          <div v-for="(recipe, idx) in topRecipes" :key="idx" class="ranking-card">
            <div class="ranking-number">{{ idx + 1 }}</div>
            <div class="ranking-details">
              <h4 class="ranking-name">{{ recipe.name }}</h4>
              <div class="ranking-score">
                <span class="score-emoji">😊</span>
                <span class="score-value">{{ recipe.rating }}%</span>
              </div>
            </div>
            <button @click="viewRecipeDetails(recipe)" class="btn-icon">→</button>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Tendances</h3>
          <p class="trend-text">Les plats italiens et gratins obtiennent les meilleures notes ce mois-ci.</p>
        </div>
      </div>
    </div>

    <!-- Modal: À améliorer -->
    <div v-if="showToImprove" class="modal-overlay" @click="showToImprove = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showToImprove = false">✕</button>
        <h2 class="modal-title">⚠️ Recettes à améliorer</h2>

        <div class="improve-list">
          <div v-for="(recipe, idx) in toImprove" :key="idx" class="improve-card">
            <div class="improve-header">
              <h4 class="improve-name">{{ idx + 1 }}. {{ recipe.name }}</h4>
              <span class="improve-score">😐 {{ recipe.rating }}%</span>
            </div>

            <div class="improve-feedback">
              <span class="feedback-label">Retours principaux :</span>
              <span class="feedback-text">{{ recipe.feedback }}</span>
              <span class="feedback-count">({{ recipe.comments }} écoles)</span>
            </div>

            <button @click="viewRecipeDetails(recipe)" class="btn-secondary-small">📊 Voir les détails</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Retours des écoles -->
    <div v-if="showSchoolFeedback" class="modal-overlay" @click="showSchoolFeedback = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSchoolFeedback = false">✕</button>
        <h2 class="modal-title">📢 Retours des écoles</h2>

        <div class="feedback-list">
          <div class="feedback-card feedback-positive">
            <div class="feedback-school">
              <strong>École Victor Hugo</strong>
              <span class="feedback-time">Aujourd'hui 14h30</span>
            </div>
            <p class="feedback-message">Gratin très apprécié par les enfants ✓</p>
          </div>

          <div class="feedback-card feedback-negative">
            <div class="feedback-school">
              <strong>Ecole Primaire de Lanta</strong>
              <span class="feedback-time">Aujourd'hui 14h15</span>
            </div>
            <p class="feedback-message">Poulet trop sec - Suggestion : cuisson plus courte</p>
          </div>

          <div class="feedback-card feedback-neutral">
            <div class="feedback-school">
              <strong>École Marie Curie</strong>
              <span class="feedback-time">Aujourd'hui 13h45</span>
            </div>
            <p class="feedback-message">Portions de riz un peu grandes pour les CP</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Planification -->
    <div v-if="showPlanning" class="modal-overlay" @click="showPlanning = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showPlanning = false">✕</button>
        <h2 class="modal-title">🗓️ Planification</h2>

        <div class="detail-section">
          <h3 class="section-title">Prochains menus</h3>

          <div class="planning-card">
            <div class="planning-date">Jeudi 8 janvier</div>
            <div class="menu-list">
              <div class="menu-item">
                <span class="menu-label">Entrée</span>
                <span class="menu-value">Taboulé</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Plat principal</span>
                <span class="menu-value">Sauté de porc* sauce pruneaux</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Accompagnement</span>
                <span class="menu-value">Carottes vichy</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Produit laitier</span>
                <span class="menu-value">Yaourt nature BIO</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Dessert</span>
                <span class="menu-value">Compote poire</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Sans viande</span>
                <span class="menu-value">Colin sauce pruneaux</span>
              </div>
            </div>
            <div class="planning-stats">
              <span class="planning-history">Historique : 😊 78%</span>
              <span class="planning-comments">4 remarques</span>
            </div>
            <button class="btn-secondary-small">Voir les remarques précédentes</button>
          </div>

          <div class="planning-card">
            <div class="planning-date">Vendredi 9 janvier</div>
            <div class="menu-list">
              <div class="menu-item">
                <span class="menu-label">Entrée</span>
                <span class="menu-value">Taboulé</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Plat principal</span>
                <span class="menu-value">Sauté de porc* sauce pruneaux</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Accompagnement</span>
                <span class="menu-value">Carottes vichy</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Produit laitier</span>
                <span class="menu-value">Yaourt nature BIO</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Dessert</span>
                <span class="menu-value">Compote poire</span>
              </div>
              <div class="menu-item">
                <span class="menu-label">Sans viande</span>
                <span class="menu-value">Colin sauce pruneaux</span>
              </div>
            </div>
            <div class="planning-stats">
              <span class="planning-history">Historique : 😐 58%</span>
              <span class="planning-alert">⚠️ À surveiller</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Gaspillage réseau -->
    <div v-if="showWasteStats" class="modal-overlay" @click="showWasteStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showWasteStats = false">✕</button>
        <h2 class="modal-title">♻️ Gaspillage réseau</h2>

        <div class="stats-row">
          <div class="stat-card stat-orange">
            <div class="stat-icon">🗑️</div>
            <div class="stat-value">34.5kg</div>
            <div class="stat-label">Aujourd'hui (8 écoles)</div>
          </div>
          <div class="stat-card stat-green">
            <div class="stat-icon">📉</div>
            <div class="stat-value">-15%</div>
            <div class="stat-label">vs semaine dernière</div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Par école</h3>
          <div class="school-waste-list">
            <div class="school-waste-item">
              <span class="school-name">Ecole Primaire de Lanta</span>
              <span class="waste-amount">5.2kg</span>
            </div>
            <div class="school-waste-item">
              <span class="school-name">École Victor Hugo</span>
              <span class="waste-amount">4.8kg</span>
            </div>
            <div class="school-waste-item">
              <span class="school-name">École Marie Curie</span>
              <span class="waste-amount">3.9kg</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CookDashboard2",
  data() {
    return {
      showTopRecipes: false,
      showToImprove: false,
      showSchoolFeedback: false,
      showPlanning: false,
      showWasteStats: false,
      showAllStats: false,
      topRecipes: [
        { name: "Lasagnes bolognaise", rating: 92 },
        { name: "Couscous poulet", rating: 87 },
        { name: "Gratin dauphinois", rating: 85 },
      ],
      toImprove: [
        { name: "Poisson pané", rating: 58, comments: 12, feedback: '"Trop sec"' },
        { name: "Purée de légumes", rating: 61, comments: 8, feedback: '"Texture"' },
      ],
    }
  },
  methods: {
    viewRecipeDetails(recipe) {
      alert(`Détails pour : ${recipe.name}`)
    },
  },
}
</script>

<style scoped>
.cook-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-featured {
  border: 2px solid #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

.card-link {
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
}

/* Ranking */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.ranking-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
}

.ranking-number {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
}

.ranking-details {
  flex: 1;
}

.ranking-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.ranking-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-emoji {
  font-size: 1.25rem;
}

.score-value {
  font-weight: 700;
  color: #22c55e;
}

.btn-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
}

/* Improve */
.improve-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.improve-card {
  background: #fff7ed;
  padding: 1.25rem;
  border-radius: 12px;
  border-left: 4px solid #f97316;
}

.improve-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.improve-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.improve-score {
  font-weight: 700;
  color: #f97316;
}

.improve-feedback {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.feedback-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.feedback-text {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.feedback-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.btn-secondary-small {
  background: white;
  color: #2563eb;
  border: 2px solid #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

/* Feedback écoles */
.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feedback-card {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.feedback-positive {
  background: #f0fdf4;
  border-color: #22c55e;
}

.feedback-negative {
  background: #fef2f2;
  border-color: #ef4444;
}

.feedback-neutral {
  background: #eff6ff;
  border-color: #2563eb;
}

.feedback-school {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.feedback-time {
  font-size: 0.75rem;
  color: #6b7280;
}

.feedback-message {
  font-size: 0.875rem;
  color: #1a1a1a;
  margin: 0;
}

/* Planning */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.planning-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.planning-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 0.5rem;
}

.planning-menu strong {
  display: block;
  font-size: 1rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.planning-stats {
  display: flex;
  gap: 1rem;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}

.planning-history {
  color: #6b7280;
}

.planning-alert {
  color: #f97316;
  font-weight: 600;
}

.trend-text {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-orange {
  border-color: #fdba74;
  background: #fff7ed;
}
.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.school-waste-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.school-waste-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.school-name {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.waste-amount {
  font-size: 0.875rem;
  font-weight: 700;
  color: #f97316;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
</style>
